import { useState, useRef } from 'react'
import { useStore } from '../store/useStore'
import { Plus, X, AlertCircle } from 'lucide-react'
import { Tooltip } from './Tooltip'
import RichTextEditor from './RichTextEditor'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import VoiceToTasks from './VoiceToTasks'
import { useTranslation } from 'react-i18next'

const MAX_TITLE_LENGTH = 500

export default function AddTodoForm() {
  const { t } = useTranslation()
  const { addTodo, projects, selectedProjectId } = useStore()
  const { soundEnabled } = useFeedbackSettings()
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [projectId, setProjectId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [titleError, setTitleError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showProjectWarning, setShowProjectWarning] = useState(false)
  const startDateInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    setTitle(newTitle)

    if (newTitle.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${newTitle.length}文字）`)
    } else {
      setTitleError('')
    }
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value
    setSelectedDate(newDate)
    updateDueDate(newDate, selectedTime)
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value
    setSelectedTime(newTime)
    updateDueDate(selectedDate, newTime)
  }

  const updateDueDate = (date: string, time: string) => {
    if (!date) {
      setDueDate('')
      return
    }

    if (time) {
      setDueDate(`${date}T${time}:00`)
    } else {
      setDueDate(`${date}T00:00:00`)
    }
  }

  const clearDate = () => {
    setSelectedDate('')
    setSelectedTime('')
    setDueDate('')
  }

  const clearStartDate = () => {
    setStartDate('')
  }

  const getStartDateValue = () => {
    if (!startDate) return null
    return `${startDate}T00:00:00`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || isSubmitting) return

    // 文字数チェック
    if (title.length > MAX_TITLE_LENGTH) {
      setTitleError(`タスク名は${MAX_TITLE_LENGTH}文字以内で入力してください（現在: ${title.length}文字）`)
      return
    }

    setIsSubmitting(true)
    try {
      // ユーザー操作内でAudioContextを初期化
      initializeFeedback()

      // プロジェクトIDの決定（全プロジェクト選択時はエラー）
      const targetProjectId = projectId || (selectedProjectId !== 'all' ? selectedProjectId : null)
      if (!targetProjectId) {
        setShowProjectWarning(true)
        setIsSubmitting(false)
        return
      }

      await addTodo({
        title: title.trim(),
        description: description.trim() || null,
        priority: 3,  // デフォルト値を保持（後でカラム削除時に対応）
        status: 'pending',
        project_id: targetProjectId,
        start_date: getStartDateValue(),
        due_date: dueDate || null,  // Already in local ISO format from updateDueDate
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small',
        assigned_to_user_id: null,
        assignment_status: 'unassigned',
        progress_percentage: 0
      })

      // タスク作成時の効果音フィードバック
      triggerFeedback('create', { soundEnabled })

      setTitle('')
      setDescription('')
      setProjectId('')
      setStartDate('')
      setDueDate('')
      setSelectedDate('')
      setSelectedTime('')
      setTitleError('')
      setIsOpen(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    setTitle('')
    setDescription('')
    setProjectId('')
    setStartDate('')
    setDueDate('')
    setSelectedDate('')
    setSelectedTime('')
    setTitleError('')
    setIsOpen(false)
  }

  if (!isOpen) {
    return (
      <div className="px-2 py-1 sm:p-4 sm:border-b">
        <div className="flex items-center gap-1 sm:gap-2">
          <VoiceToTasks compact />
          <Tooltip content="クリックしてタスクを追加">
            <button
              onClick={() => setIsOpen(true)}
              data-add-todo-button
              data-tour="add-task"
              className="flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-4 sm:py-3 text-xs sm:text-base bg-indigo-600 text-white rounded-md sm:rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
              新しいToDoを追加
            </button>
          </Tooltip>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="p-2 sm:p-4 bg-gray-50 sm:border-b">
      <div className="space-y-2 sm:space-y-4">
        <div>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="タスク名を入力..."
            className={`w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 ${
              titleError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-indigo-500'
            }`}
            autoFocus
            required
          />
          {titleError && (
            <p className="text-red-600 text-xs sm:text-sm mt-1">{titleError}</p>
          )}
          {!titleError && title.length > MAX_TITLE_LENGTH * 0.8 && (
            <p className="text-yellow-600 text-xs sm:text-sm mt-1">
              残り {MAX_TITLE_LENGTH - title.length} 文字
            </p>
          )}
        </div>

        <div>
          <RichTextEditor
            content={description}
            onChange={setDescription}
            placeholder="詳細（任意）"
          />
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              プロジェクト
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">
                {selectedProjectId
                  ? projects.find(p => p.id === selectedProjectId)?.name || t('project.selectProject')
                  : t('project.selectProject')}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* 期間設定 */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              期間（任意）
            </label>
            <div className="flex items-center gap-2">
              {/* 開始日 */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">開始日</label>
                <div className="flex items-center gap-1">
                  <div
                    className="relative cursor-pointer flex-1"
                    onClick={() => startDateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={startDateInputRef}
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={selectedDate || undefined}
                      className="w-full h-9 px-2 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-9 px-2 text-xs sm:text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                      {startDate || 'YYYY-MM-DD'}
                    </div>
                  </div>
                  {startDate && (
                    <button
                      type="button"
                      onClick={clearStartDate}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="開始日をクリア"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              <span className="text-gray-400 mt-5">〜</span>

              {/* 期限 */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">期限</label>
                <div className="flex items-center gap-1">
                  <div
                    className="relative cursor-pointer flex-1"
                    onClick={() => dateInputRef.current?.showPicker?.()}
                  >
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      min={startDate || undefined}
                      className="w-full h-9 px-2 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                      style={{ colorScheme: 'light' }}
                    />
                    <div className="h-9 px-2 text-xs sm:text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                      {selectedDate || 'YYYY-MM-DD'}
                    </div>
                  </div>
                  {selectedDate && (
                    <button
                      type="button"
                      onClick={clearDate}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="期限をクリア"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 時間選択（日付選択後に表示） */}
            {selectedDate && (
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">
                  期限時刻（任意）
                </label>
                <div
                  className="relative cursor-pointer"
                  style={{ width: '120px' }}
                  onClick={() => timeInputRef.current?.showPicker?.()}
                >
                  <input
                    type="time"
                    ref={timeInputRef}
                    value={selectedTime}
                    onChange={handleTimeChange}
                    className="w-full h-9 px-3 text-sm border rounded-lg opacity-0 absolute inset-0 cursor-pointer z-0"
                    style={{ colorScheme: 'light' }}
                  />
                  <div className="h-9 px-3 text-sm border rounded-lg bg-gray-50 flex items-center pointer-events-none">
                    {selectedTime || '--:--'}
                  </div>
                </div>
              </div>
            )}

            {/* ボタン行 */}
            <div className="flex justify-end gap-2 mt-4">
              <Tooltip content="キャンセル">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </Tooltip>
              <Tooltip content="タスクを追加">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 sm:px-6 sm:py-2 text-sm sm:text-base bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '追加中...' : '追加'}
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* カスタムプロジェクト選択警告ダイアログ */}
      {showProjectWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl">
            <div className="flex items-start mb-4">
              <AlertCircle className="h-6 w-6 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('project.selectProjectTitle')}</h3>
                <p className="text-sm text-gray-600">
                  {t('project.selectProjectRequired')}
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowProjectWarning(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}